import { Permission } from '../auth/permissions'
import { eventPhase, EventTimes } from './event-edit-lock'

/**
 * How much a delete would erase (docs/authorization.md, _Deleting_):
 *
 * - `unused` - nothing depends on it yet: it has not started and nobody is booked on it. Whoever may create it may
 *   delete it.
 * - `used` - it is part of the record: it started or ended, a customer is booked on it, or it was cancelled. Client
 *   statistics, history and attendance count it, so deleting needs `…purge:entity`, and cancelling is the action to
 *   reach for instead.
 */
export type DeletionScope = 'unused' | 'used'

export interface ReservationDeletionFacts {
  start: Date | string
  cancelledAt?: Date | string | null
  /** Whether any customer is on it: the booked one, or the one a manager picked. */
  hasCustomer: boolean
}

export function reservationDeletion(reservation: ReservationDeletionFacts, now: Date): DeletionScope {
  if (reservation.cancelledAt || reservation.hasCustomer) {
    return 'used'
  }
  return new Date(reservation.start) <= now ? 'used' : 'unused'
}

/**
 * `participations` counts every participation row of the event, cancelled ones included: a cancelled sign-up is
 * still part of that client's history. An event with no times at all has never happened, so only its participations
 * decide.
 */
export function eventDeletion(event: EventTimes, participations: number, now: Date, timezone: string): DeletionScope {
  if (participations > 0) {
    return 'used'
  }
  const phase = eventPhase(event, now, timezone)
  return phase === null || phase === 'upcoming' ? 'unused' : 'used'
}

const DELETE_PERMISSIONS = {
  reservation: { unused: 'reservation.delete:entity', used: 'reservation.purge:entity' },
  event: { unused: 'event.delete:entity', used: 'event.purge:entity' },
} as const satisfies Record<string, Record<DeletionScope, Permission>>

/** The permission a delete of this scope needs. The backend enforces it; the frontend hides the action without it. */
export function requiredDeletePermission(kind: keyof typeof DELETE_PERMISSIONS, scope: DeletionScope): Permission {
  return DELETE_PERMISSIONS[kind][scope]
}
